import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { gql, useQuery } from '@apollo/client';
import { ChevronDown, Check, Loader2, X, ExternalLink, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { VehicleViewModel } from '@/graphql/generated';
import { getVehicleDisplayName, getVehicleSecondaryText } from './vehicle-autocomplete';
import {
	EntityDetailsModal,
	supportsEntityDetails,
} from './entity-details-modal';

export interface CustomerVehicleAutocompleteProps {
	customerId: string;
	value: string;
	onChange: (value: string, vehicle: VehicleViewModel | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	initialSelectedLabel?: string;
	openDelay?: number;
	autoFocus?: boolean;
	/** When provided, replaces the clear (X) button with a delete (trash) action */
	onDelete?: () => void;
}

/**
 * GraphQL query that fetches ALL vehicles associated with a customer.
 * Returns a plain array — no filtering, paging, or sorting on server.
 * Client fetches once per customerId and filters locally.
 */
const CUSTOMER_ASSOCIATED_VEHICLES_QUERY = gql`
	query CustomerAssociatedVehicles($customerId: Ulid!) {
		allAssociatedVehicles(customerId: $customerId) {
			vehicleId
			plateNumber
			make
			model
			color
			vehicleType {
				vehicleTypeId
				name
			}
			isActive
		}
	}
`;

/**
 * Local filter: matches search term against plateNumber, make, and model (case-insensitive).
 */
const matchesSearch = (vehicle: VehicleViewModel, term: string): boolean => {
	const lower = term.toLowerCase();
	return (
		(vehicle.plateNumber?.toLowerCase().includes(lower) ?? false) ||
		(vehicle.make?.toLowerCase().includes(lower) ?? false) ||
		(vehicle.model?.toLowerCase().includes(lower) ?? false)
	);
};

/**
 * Sort vehicles by plateNumber ascending (null-safe).
 */
const sortByPlateNumber = (a: VehicleViewModel, b: VehicleViewModel): number =>
	(a.plateNumber ?? '').localeCompare(b.plateNumber ?? '');

/**
 * Vehicle autocomplete scoped to a specific customer and its beneficiaries.
 * Fetches all vehicles once via `allAssociatedVehicles` and filters locally.
 *
 * The component is disabled when no `customerId` is provided.
 */
export const CustomerVehicleAutocomplete: React.FC<CustomerVehicleAutocompleteProps> = ({
	customerId,
	value,
	onChange,
	placeholder = 'Search customer vehicles...',
	disabled = false,
	className = '',
	initialSelectedLabel,
	openDelay = 0,
	autoFocus = false,
	onDelete,
}) => {
	const { t } = useTranslation('common');
	const isDisabled = disabled || !customerId;

	// ── State ────────────────────────────────────────────────────
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedItem, setSelectedItem] = useState<VehicleViewModel | null>(null);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	// Dropdown position for portal rendering
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
		openUpwards: false,
	});

	// Entity details modal state
	const [detailsModalOpen, setDetailsModalOpen] = useState(false);
	const [detailsEntityId, setDetailsEntityId] = useState<string | null>(null);
	const [detailsEntityName, setDetailsEntityName] = useState<string | null>(null);
	const [isOpeningModal, setIsOpeningModal] = useState(false);

	// Refs
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const preventDropdownCloseRef = useRef(false);
	const openDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hasOpenedRef = useRef(false);

	// ── Fetch all vehicles for customer (once) ──────────────────
	const { data, loading, error } = useQuery(CUSTOMER_ASSOCIATED_VEHICLES_QUERY, {
		variables: { customerId },
		skip: !customerId,
		fetchPolicy: 'cache-and-network',
	});

	const allVehicles: VehicleViewModel[] = useMemo(
		() => (data?.allAssociatedVehicles ?? []).slice().sort(sortByPlateNumber),
		[data],
	);

	// ── Local filtering ─────────────────────────────────────────
	const filteredItems = useMemo(() => {
		const term = searchTerm.trim();
		if (!term) return allVehicles;
		return allVehicles.filter((v) => matchesSearch(v, term));
	}, [allVehicles, searchTerm]);

	// ── Helpers ─────────────────────────────────────────────────
	const openDropdown = useCallback(() => {
		if (isOpen) return;
		if (openDelayTimeoutRef.current) clearTimeout(openDelayTimeoutRef.current);

		if (openDelay > 0 && !hasOpenedRef.current) {
			openDelayTimeoutRef.current = setTimeout(() => {
				setIsOpen(true);
				hasOpenedRef.current = true;
			}, openDelay);
		} else {
			setIsOpen(true);
			hasOpenedRef.current = true;
		}
	}, [isOpen, openDelay]);

	useEffect(() => {
		return () => {
			if (openDelayTimeoutRef.current) clearTimeout(openDelayTimeoutRef.current);
		};
	}, []);

	// Reset highlighted index when dropdown opens or items change
	useEffect(() => {
		if (isOpen) setHighlightedIndex(-1);
	}, [isOpen]);

	// Scroll highlighted item into view
	useEffect(() => {
		if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
			optionRefs.current[highlightedIndex]?.scrollIntoView({
				block: 'nearest',
				behavior: 'smooth',
			});
		}
	}, [highlightedIndex]);

	// Sync selectedItem with external value
	useEffect(() => {
		if (!value) {
			if (selectedItem) setSelectedItem(null);
			return;
		}
		const selectedId = selectedItem?.vehicleId ?? null;
		if (selectedId === value) return;
		const match = allVehicles.find((v) => v.vehicleId === value);
		if (match) setSelectedItem(match);
		else if (selectedItem) setSelectedItem(null);
	}, [value, allVehicles, selectedItem]);

	// ── Selection / Clear ───────────────────────────────────────
	const handleItemSelect = (item: VehicleViewModel) => {
		setSelectedItem(item);
		setSearchTerm('');
		setIsOpen(false);
		onChange(item.vehicleId, item);
	};

	const handleClear = () => {
		setSelectedItem(null);
		setSearchTerm('');
		onChange('', null);
	};

	const getDisplayValue = () => {
		if (selectedItem) return getVehicleDisplayName(selectedItem);
		if (value && initialSelectedLabel) return initialSelectedLabel;
		return placeholder;
	};

	// ── Entity details modal ────────────────────────────────────
	const showDetails = supportsEntityDetails('vehicles');

	const openDetailsModal = (entityId: string, entityName?: string | null) => {
		const wasDropdownOpen = isOpen;
		preventDropdownCloseRef.current = true;
		setIsOpeningModal(true);
		setDetailsModalOpen(true);
		setDetailsEntityId(entityId);
		setDetailsEntityName(entityName ?? null);
		if (wasDropdownOpen) setTimeout(() => setIsOpen(true), 0);
		setTimeout(() => setIsOpeningModal(false), 100);
		setTimeout(() => { preventDropdownCloseRef.current = false; }, 300);
	};

	const handleShowDetails = (item: VehicleViewModel, event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		openDetailsModal(item.vehicleId, getVehicleDisplayName(item));
	};

	const handleShowDetailsFromValue = (event: React.MouseEvent) => {
		if (!value) return;
		event.stopPropagation();
		event.preventDefault();
		const name = selectedItem ? getVehicleDisplayName(selectedItem) : initialSelectedLabel || null;
		openDetailsModal(value, name);
	};

	const handleCloseDetails = () => {
		setDetailsModalOpen(false);
		setDetailsEntityId(null);
		setDetailsEntityName(null);
		setIsOpeningModal(false);
		preventDropdownCloseRef.current = false;
	};

	// ── Click-outside / ESC handling ────────────────────────────
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (preventDropdownCloseRef.current) return;
			if (detailsModalOpen || isOpeningModal) return;
			const outsideInput = inputRef.current && !inputRef.current.contains(event.target as Node);
			const outsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target as Node);
			if (outsideInput && outsideDropdown) setIsOpen(false);
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [detailsModalOpen, isOpeningModal]);

	useEffect(() => {
		const handleGlobalKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && (detailsModalOpen || isOpeningModal) && isOpen) {
				event.stopPropagation();
			}
		};
		if (detailsModalOpen || isOpeningModal) {
			document.addEventListener('keydown', handleGlobalKeyDown, true);
			return () => document.removeEventListener('keydown', handleGlobalKeyDown, true);
		}
	}, [detailsModalOpen, isOpeningModal, isOpen]);

	// ── Dropdown position ───────────────────────────────────────
	useEffect(() => {
		const updatePosition = () => {
			if (inputRef.current && isOpen) {
				const rect = inputRef.current.getBoundingClientRect();
				const dropdownMaxHeight = 320;
				const offset = 4;
				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;

				const spaceBelow = viewportHeight - rect.bottom;
				const spaceAbove = rect.top;
				const shouldOpenUpwards = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

				let left = rect.left + window.scrollX;
				const dropdownWidth = rect.width;
				if (rect.left + dropdownWidth > viewportWidth) left = viewportWidth - dropdownWidth - 8 + window.scrollX;
				if (left < window.scrollX) left = 8 + window.scrollX;

				setDropdownPosition({
					top: shouldOpenUpwards ? rect.top + window.scrollY - offset : rect.bottom + window.scrollY + offset,
					left,
					width: rect.width,
					openUpwards: shouldOpenUpwards,
				});
			}
		};

		if (isOpen) {
			updatePosition();
			window.addEventListener('resize', updatePosition);
			window.addEventListener('scroll', updatePosition, true);
			return () => {
				window.removeEventListener('resize', updatePosition);
				window.removeEventListener('scroll', updatePosition, true);
			};
		} else {
			setDropdownPosition({ top: 0, left: 0, width: 0, openUpwards: false });
		}
	}, [isOpen]);

	// ── AutoFocus ───────────────────────────────────────────────
	useEffect(() => {
		if (autoFocus && inputRef.current) {
			const id = setTimeout(() => inputRef.current?.focus(), 0);
			return () => clearTimeout(id);
		}
	}, [autoFocus]);

	// ── Render ──────────────────────────────────────────────────
	return (
		<div className={`relative ${className}`}>
			{/* Input Field */}
			<div className="relative">
				<input
					ref={inputRef}
					type="text"
					value={isOpen ? searchTerm : getDisplayValue()}
					onChange={(e) => {
						setSearchTerm(e.target.value);
						if (!isOpen) openDropdown();
						if (e.target.value === '' && selectedItem) handleClear();
					}}
					onFocus={() => {
						if (!isDisabled) {
							setSearchTerm('');
							openDropdown();
						}
					}}
					onBlur={() => {
						setTimeout(() => {
							if (preventDropdownCloseRef.current) return;
							if (detailsModalOpen || isOpeningModal) return;
							if (!dropdownRef.current?.contains(document.activeElement)) {
								setIsOpen(false);
								setSearchTerm('');
							}
						}, 150);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Escape') {
							if (preventDropdownCloseRef.current) return;
							if (detailsModalOpen || isOpeningModal) return;
							setIsOpen(false);
							setSearchTerm('');
							setHighlightedIndex(-1);
							e.preventDefault();
						} else if (e.key === 'ArrowDown') {
							e.preventDefault();
							if (!isOpen) {
								setSearchTerm('');
								openDropdown();
							} else if (filteredItems.length > 0) {
								setHighlightedIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : prev));
							}
						} else if (e.key === 'ArrowUp') {
							e.preventDefault();
							if (isOpen && filteredItems.length > 0) {
								setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
							}
						} else if (e.key === 'Enter' && isOpen) {
							e.preventDefault();
							if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
								handleItemSelect(filteredItems[highlightedIndex]);
							}
						}
					}}
					placeholder={isDisabled && !customerId ? 'Select a customer first...' : placeholder}
					disabled={isDisabled}
					className={`w-full h-9 px-3 py-1 pr-12 bg-[#fafafa] border border-input rounded-md shadow-xs text-base md:text-sm font-semibold placeholder:text-placeholder placeholder:font-light placeholder-shown:font-normal transition-[color,box-shadow] dark:bg-slate-900 ${
						isDisabled
							? 'opacity-50 cursor-not-allowed'
							: 'hover:border-input focus:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]'
					} ${!selectedItem && !value && !searchTerm && !isOpen ? 'text-muted-foreground' : 'text-foreground'}`}
				/>
				<div className="absolute flex items-center gap-1 transform -translate-y-1/2 right-1 top-1/2">
					{showDetails && !isDisabled && (selectedItem || value) && (
						<div
							role="button"
							tabIndex={0}
							onClick={(e) =>
								selectedItem ? handleShowDetails(selectedItem, e) : handleShowDetailsFromValue(e)
							}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									e.stopPropagation();
									const syntheticEvent = { ...e, preventDefault: e.preventDefault.bind(e), stopPropagation: e.stopPropagation.bind(e) } as unknown as React.MouseEvent;
									if (selectedItem) handleShowDetails(selectedItem, syntheticEvent);
									else handleShowDetailsFromValue(syntheticEvent);
								}
							}}
							className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-primary"
							title={t('labels.view_details')}
						>
							<ExternalLink className="w-4 h-4 opacity-60 hover:opacity-100" />
						</div>
					)}
					{(selectedItem || (value && initialSelectedLabel)) && !isDisabled && !onDelete && (
						<div
							role="button"
							tabIndex={0}
							onClick={(e) => {
								e.stopPropagation();
								handleClear();
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									e.stopPropagation();
									handleClear();
								}
							}}
							className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-foreground"
							title={t('placeholders.clear_selection')}
						>
							<X className="w-4 h-4" />
						</div>
					)}
					{onDelete && !isDisabled && (
						<div
							role="button"
							tabIndex={0}
							onClick={(e) => {
								e.stopPropagation();
								onDelete();
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									e.stopPropagation();
									onDelete();
								}
							}}
							className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-destructive"
							title={t('common:buttons.delete', 'Delete')}
						>
							<Trash2 className="w-4 h-4" />
						</div>
					)}
					<div
						role="button"
						tabIndex={0}
						onClick={(e) => {
							e.stopPropagation();
							if (!isDisabled) {
								if (isOpen) {
									setIsOpen(false);
									setSearchTerm('');
								} else {
									setSearchTerm('');
									openDropdown();
								}
							}
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								e.stopPropagation();
								if (!isDisabled) {
									if (isOpen) {
										setIsOpen(false);
										setSearchTerm('');
									} else {
										setSearchTerm('');
										openDropdown();
									}
								}
							}
						}}
						className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-foreground"
						title={isOpen ? 'Close dropdown' : 'Open dropdown'}
					>
						<ChevronDown
							className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
						/>
					</div>
				</div>
			</div>

			{/* Dropdown Menu — Portal */}
			{isOpen &&
				!isDisabled &&
				dropdownPosition.width > 0 &&
				createPortal(
					<div
						className={`fixed z-50 overflow-hidden duration-200 bg-popover border border-border rounded-md shadow-md max-h-80 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ${
							dropdownPosition.openUpwards ? 'origin-bottom' : 'origin-top'
						}`}
						style={{
							top: dropdownPosition.openUpwards ? 'auto' : `${dropdownPosition.top}px`,
							bottom: dropdownPosition.openUpwards
								? `${window.innerHeight - dropdownPosition.top}px`
								: 'auto',
							left: `${dropdownPosition.left}px`,
							width: `${dropdownPosition.width}px`,
							pointerEvents: 'auto',
						}}
						ref={dropdownRef}
						onWheel={(e) => e.stopPropagation()}
						onTouchMove={(e) => e.stopPropagation()}
						onClick={(e) => e.stopPropagation()}
						onMouseDown={(e) => {
							e.stopPropagation();
							const target = e.currentTarget;
							const isScrollbar =
								target.clientWidth < target.offsetWidth &&
								e.nativeEvent.offsetX > target.clientWidth;
							if (!isScrollbar) {
								e.preventDefault();
							} else {
								preventDropdownCloseRef.current = true;
								const handleMouseUp = () => {
									preventDropdownCloseRef.current = false;
									document.removeEventListener('mouseup', handleMouseUp);
								};
								document.addEventListener('mouseup', handleMouseUp);
							}
						}}
					>
						<div className="overflow-y-auto max-h-60">
							{loading && allVehicles.length === 0 && (
								<div className="flex items-center justify-center px-3 py-6 text-sm text-muted-foreground">
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									Loading vehicles...
								</div>
							)}

							{error && (
								<div className="px-3 py-6 text-sm text-center text-destructive">
									Error loading vehicles. Please try again.
								</div>
							)}

							{!loading && !error && filteredItems.length === 0 && searchTerm.trim() && (
								<div className="px-3 py-6 text-sm text-center text-muted-foreground">
									No vehicles found for "{searchTerm.trim()}"
								</div>
							)}

							{!loading && !error && allVehicles.length === 0 && !searchTerm.trim() && (
								<div className="px-3 py-6 text-sm text-center text-muted-foreground">
									No vehicles found
								</div>
							)}

							{filteredItems.map((item, index) => {
								const itemId = item.vehicleId;
								const currentSelectedId = selectedItem?.vehicleId ?? value ?? '';
								const isSelected = currentSelectedId === itemId;
								const isHighlighted = index === highlightedIndex;
								const displayName = getVehicleDisplayName(item);
								const secondaryText = getVehicleSecondaryText(item);

								return (
									<button
										key={itemId}
										ref={(el) => {
											optionRefs.current[index] = el;
										}}
										onClick={() => handleItemSelect(item)}
										onMouseEnter={() => setHighlightedIndex(index)}
										className={`w-full text-left px-3 py-3 text-sm transition-colors duration-150 ${
											isSelected
												? 'bg-accent dark:bg-gray-800 text-accent-foreground border-r-2 border-primary'
												: isHighlighted
													? 'bg-accent/60 text-accent-foreground bg-gray-100 dark:bg-gray-800'
													: 'text-foreground hover:bg-accent/60 hover:text-accent-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
										}`}
									>
										<div className="flex items-center gap-3">
											<div className="flex justify-center flex-shrink-0 w-6">
												{isSelected && <Check className="w-5 h-5 text-primary" />}
											</div>
											<div className="flex-1 min-w-0">
												<div className="font-medium truncate">{displayName}</div>
												{secondaryText && (
													<div className="text-xs text-muted-foreground truncate mt-0.5">
														{secondaryText}
													</div>
												)}
											</div>
											<div className="flex items-center justify-end gap-2 flex-shrink-0 ml-4 min-w-[2rem]">
												{showDetails && (
													<div
														role="button"
														tabIndex={0}
														onClick={(e) => {
															e.stopPropagation();
															handleShowDetails(item, e);
														}}
														onKeyDown={(e) => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault();
																e.stopPropagation();
																const syntheticEvent = { ...e, preventDefault: e.preventDefault.bind(e), stopPropagation: e.stopPropagation.bind(e) } as unknown as React.MouseEvent;
																handleShowDetails(item, syntheticEvent);
															}
														}}
														className="flex items-center justify-center w-6 h-6 transition-colors cursor-pointer text-muted-foreground hover:text-primary"
														title={t('labels.view_details')}
													>
														<ExternalLink className="w-4 h-4 opacity-60 hover:opacity-100" />
													</div>
												)}
											</div>
										</div>
									</button>
								);
							})}
						</div>
					</div>,
					document.body,
				)}

			{/* Entity Details Modal */}
			{detailsModalOpen && detailsEntityId && (
				<EntityDetailsModal
					isOpen={detailsModalOpen}
					onClose={handleCloseDetails}
					entityType="vehicles"
					entityId={detailsEntityId}
					entityDisplayName={detailsEntityName || undefined}
				/>
			)}
		</div>
	);
};
